import { useEffect, useRef } from "react";
import {
    Loader2, ShieldAlert
} from "lucide-react";
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
    Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
    formatBytes, getSeverityStyle, CustomTooltip
} from "./FirewallLogs";

interface AppsTabProps {
    appsData: any;
    appsLoading: boolean;
    onRefresh?: () => void;
}

export default function AppsTab({
    appsData,
    appsLoading,
    onRefresh
}: AppsTabProps) {
    const refreshRef = useRef(onRefresh);
    useEffect(() => {
        refreshRef.current = onRefresh;
    }, [onRefresh]);

    // Poll apps API every 10 seconds while on Apps tab
    useEffect(() => {
        const intervalId = setInterval(() => {
            refreshRef.current?.();
        }, 10000);

        return () => clearInterval(intervalId);
    }, []);
    return (
        <div className="animate-fade-in flex flex-col gap-6">
            {appsLoading ? (
                <div className="flex items-center justify-center py-24 gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Loading applications data...</span>
                </div>
            ) : !appsData ? (
                <div className="flex items-center justify-center py-24 text-xs text-slate-500 font-bold uppercase tracking-wider">No Application Data Available</div>
            ) : (
                <>
                    {/* Top row with charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Risk Distribution Donut */}
                        <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                            <h3 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest mb-6">App Risk Distribution</h3>
                            <div className="flex items-center justify-between gap-4 h-[240px] flex-wrap md:flex-nowrap">
                                <div className="flex-1 h-full min-w-[140px] max-w-[240px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={appsData.risk || []}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={4}
                                                dataKey="sessions"
                                            >
                                                {(appsData.risk || []).map((entry: any, index: number) => {
                                                    const risk = String(entry.risk).toLowerCase();
                                                    let color = "#475569"; // unrated
                                                    if (risk === "elevated") color = "#f59e0b";
                                                    if (risk === "medium") color = "#3b82f6";
                                                    if (risk === "low") color = "#10b981";
                                                    if (risk === "high") color = "#ef4444";
                                                    if (risk === "critical") color = "#991b1b";
                                                    return <Cell key={`cell-${index}`} fill={color} stroke="none" />;
                                                })}
                                            </Pie>
                                            <RechartsTooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex flex-col gap-2 shrink-0 max-h-[220px] overflow-y-auto custom-scroll w-full md:w-auto">
                                    {(appsData.risk || []).map((entry: any, index: number) => {
                                        const risk = String(entry.risk).toLowerCase();
                                        let color = "bg-slate-600";
                                        if (risk === "elevated") color = "bg-amber-500";
                                        if (risk === "medium") color = "bg-blue-500";
                                        if (risk === "low") color = "bg-emerald-500";
                                        if (risk === "high") color = "bg-red-500";
                                        if (risk === "critical") color = "bg-red-800";
                                        return (
                                            <div key={index} className="flex items-center justify-between gap-6 text-[10px]">
                                                <div className="flex items-center gap-2 w-20">
                                                    <span className={`w-2 h-2 rounded-full ${color}`} />
                                                    <span className="font-bold text-slate-600 dark:text-slate-400 capitalize">{entry.risk}</span>
                                                </div>
                                                <span className="font-mono text-slate-800 dark:text-slate-300 font-bold">
                                                    {entry.sessions.toLocaleString()} | {entry.app_count} apps | {formatBytes(entry.bandwidth)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Categories Bar Chart */}
                        <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                            <h3 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest mb-6">Top Categories by Bandwidth</h3>
                            <div className="h-[240px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        layout="vertical"
                                        data={appsData.categories?.slice(0, 15) || []}
                                        margin={{ left: 20, right: 30, top: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" horizontal={false} />
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="category"
                                            type="category"
                                            tick={{ fill: '#64748b', fontSize: 9, fontWeight: 600 }}
                                            axisLine={false}
                                            tickLine={false}
                                            width={100}
                                        />
                                        <RechartsTooltip content={<CustomTooltip />} />
                                        <Bar dataKey="bandwidth" fill="#d97706" radius={[0, 4, 4, 0]} barSize={10} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Suspicious Apps Table */}
                    <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3 flex-wrap">
                            <ShieldAlert size={14} className="text-red-500" />
                            <h3 className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-widest">Suspicious / High-Risk Applications</h3>
                            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Remote access, tunneling, VPN, critical/high risk</span>
                        </div>
                        <div className="overflow-x-auto custom-scroll">
                            <table className="w-full text-xs text-left min-w-[800px]">
                                <thead className="bg-slate-50 dark:bg-slate-950/40 text-slate-600 dark:text-slate-400 uppercase tracking-widest text-[10px] font-bold border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="px-6 py-4">Application</th>
                                        <th className="px-6 py-4 text-center">Category</th>
                                        <th className="px-6 py-4 text-center">Risk</th>
                                        <th className="px-6 py-4 text-center">Sessions</th>
                                        <th className="px-6 py-4 text-right">Sent</th>
                                        <th className="px-6 py-4 text-right">Received</th>
                                        <th className="px-6 py-4">Users</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40">
                                    {!appsData.suspicious?.length ? (
                                        <tr>
                                            <td colSpan={7} className="py-8 text-center text-xs text-slate-500 font-bold uppercase">No Suspicious Applications Found</td>
                                        </tr>
                                    ) : (
                                        appsData.suspicious.map((app: any, idx: number) => (
                                            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                                                <td className="px-6 py-4 font-bold text-red-600 dark:text-red-300">{app.app}</td>
                                                <td className="px-6 py-4 text-center text-slate-600 dark:text-slate-400">{app.appcat}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border ${getSeverityStyle(app.apprisk)}`}>
                                                        {app.apprisk}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center font-mono font-bold text-gray-900 dark:text-white">{app.sessions}</td>
                                                <td className="px-6 py-4 text-right font-mono text-blue-600 dark:text-blue-400 font-bold">{formatBytes(app.sent)}</td>
                                                <td className="px-6 py-4 text-right font-mono text-emerald-600 dark:text-emerald-400 font-bold">{formatBytes(app.rcvd)}</td>
                                                <td className="px-6 py-4 flex flex-wrap gap-1.5 items-center">
                                                    {(app.users || []).slice(0, 3).map((u: string, ui: number) => (
                                                        <span key={ui} className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-500 border border-amber-500/20 text-[9px] font-bold font-mono">
                                                            {u}
                                                        </span>
                                                    ))}
                                                    {app.users?.length > 3 && <span className="text-[9px] text-slate-500 dark:text-slate-400 font-bold">+{app.users.length - 3} more</span>}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Top Applications Table */}
                    <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
                            <h3 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest">Top Applications by Bandwidth — Today</h3>
                        </div>
                        <div className="overflow-x-auto custom-scroll">
                            <table className="w-full text-xs text-left min-w-[1000px]">
                                <thead className="bg-slate-50 dark:bg-slate-950/40 text-slate-600 dark:text-slate-400 uppercase tracking-widest text-[10px] font-bold border-b border-slate-200 dark:border-slate-800 sticky top-0">
                                    <tr>
                                        <th className="px-6 py-4 w-12 text-center">#</th>
                                        <th className="px-6 py-4">Application</th>
                                        <th className="px-6 py-4 text-center">Category</th>
                                        <th className="px-6 py-4 text-center">Risk</th>
                                        <th className="px-6 py-4 text-center">Sessions</th>
                                        <th className="px-6 py-4 text-right">Sent</th>
                                        <th className="px-6 py-4 text-right">Received</th>
                                        <th className="px-6 py-4 text-right">Total</th>
                                        <th className="px-6 py-4">Users</th>
                                        <th className="px-6 py-4 w-32">Bandwidth</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/40">
                                    {!appsData.top?.length ? (
                                        <tr>
                                            <td colSpan={10} className="py-8 text-center text-xs text-slate-500 font-bold uppercase">No Applications Data</td>
                                        </tr>
                                    ) : (
                                        (() => {
                                            const maxBw = Math.max(...(appsData.top || []).map((a: any) => (a.sent || 0) + (a.rcvd || 0)), 1);
                                            return (appsData.top || []).map((app: any, idx: number) => {
                                                const total = (app.sent || 0) + (app.rcvd || 0);
                                                return (
                                                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                                                        <td className="px-6 py-4 text-center text-slate-500 font-mono">{idx + 1}</td>
                                                        <td className="px-6 py-4 font-bold text-amber-600 dark:text-amber-500">{app.app}</td>
                                                        <td className="px-6 py-4 text-center text-slate-600 dark:text-slate-400">{app.appcat}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border ${getSeverityStyle(app.apprisk)}`}>
                                                                {app.apprisk}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center font-mono font-bold text-gray-900 dark:text-white">{app.sessions}</td>
                                                        <td className="px-6 py-4 text-right font-mono text-blue-600 dark:text-blue-400">{formatBytes(app.sent)}</td>
                                                        <td className="px-6 py-4 text-right font-mono text-emerald-600 dark:text-emerald-400">{formatBytes(app.rcvd)}</td>
                                                        <td className="px-6 py-4 text-right font-mono font-bold text-slate-800 dark:text-slate-200">{formatBytes(total)}</td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex flex-wrap gap-1">
                                                                {(app.users || []).slice(0, 3).map((u: string, ui: number) => (
                                                                    <span key={ui} className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-500/80 border border-amber-500/20 text-[9px] font-bold font-mono">
                                                                        {u}
                                                                    </span>
                                                                ))}
                                                                {app.users?.length > 3 && <span className="text-[9px] text-slate-500 dark:text-slate-600 font-bold">+{app.users.length - 3}</span>}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-950 rounded-full overflow-hidden border border-slate-200 dark:border-slate-900">
                                                                <div
                                                                    className="h-full bg-gradient-to-r from-amber-500/40 to-amber-500 shadow-[0_0_8px_#f59e0b]"
                                                                    style={{ width: `${(total / maxBw) * 100}%` }}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
